import React, { useEffect, useMemo, useState } from "react";
import { FaChartLine, FaDatabase } from "react-icons/fa";
import { supabase } from "../../../supabaseClient";
import InsightsBackButton from "../shared/InsightsBackButton";
import "./MarketInsights.css";

const compact = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });
const integer = new Intl.NumberFormat("en-US");

function TimelineChart({ rows, selectedYear, onSelectYear }) {
  const width = 1100;
  const height = 390;
  const pad = { left: 62, right: 24, top: 24, bottom: 46 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const max = Math.max(...rows.map((r) => Number(r.canonical_cards) || 0), 1);
  const x = (i) => pad.left + (rows.length <= 1 ? innerW / 2 : (i / (rows.length - 1)) * innerW);
  const y = (value) => pad.top + innerH - (Number(value || 0) / max) * innerH;
  const line = rows.map((r, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(r.canonical_cards).toFixed(1)}`).join(" ");
  const area = rows.length ? `${line} L${x(rows.length - 1)},${pad.top + innerH} L${x(0)},${pad.top + innerH} Z` : "";
  const tickIndexes = rows.length <= 10 ? rows.map((_, i) => i) : [...new Set([0, ...Array.from({ length: 8 }, (_, n) => Math.round(((n + 1) * (rows.length - 1)) / 9)), rows.length - 1])];

  return (
    <div className="market-chart-wrap">
      <svg className="market-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Cards cataloged by year">
        {[0, .25, .5, .75, 1].map((ratio) => {
          const yy = pad.top + innerH - ratio * innerH;
          return <g key={ratio}><line x1={pad.left} x2={width - pad.right} y1={yy} y2={yy} className="market-grid-line" /><text x={pad.left - 10} y={yy + 4} textAnchor="end" className="market-axis-label">{compact.format(max * ratio)}</text></g>;
        })}
        {area && <path d={area} className="market-area" />}
        {line && <path d={line} className="market-line" />}
        {rows.map((row, i) => (
          <circle key={row.year} cx={x(i)} cy={y(row.canonical_cards)} r={Number(selectedYear) === Number(row.year) ? 6 : 3.2} className={Number(selectedYear) === Number(row.year) ? "market-point selected" : "market-point"} onClick={() => onSelectYear(row)}>
            <title>{row.year}: {integer.format(row.canonical_cards)} cards cataloged</title>
          </circle>
        ))}
        {tickIndexes.map((i) => <text key={i} x={x(i)} y={height - 15} textAnchor="middle" className="market-axis-label">{rows[i]?.year}</text>)}
      </svg>
    </div>
  );
}

export default function HobbyTimeline() {
  const [sports, setSports] = useState([]);
  const [sportId, setSportId] = useState("");
  const [timeline, setTimeline] = useState([]);
  const [selected, setSelected] = useState(null);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadSports = async () => {
      const { data, error: rpcError } = await supabase.rpc("get_market_insights_sports");
      if (rpcError) { console.error(rpcError); setError("Market Insights could not load the available sports."); return; }
      setSports(data || []);
    };
    loadSports();
  }, []);

  useEffect(() => {
    const loadTimeline = async () => {
      setLoading(true); setError(""); setSelected(null); setFamilies([]);
      const { data, error: rpcError } = await supabase.rpc("get_market_insights_timeline", { p_subcategory_id: sportId || null });
      if (rpcError) { console.error(rpcError); setError("Market Insights could not load the catalog timeline."); setTimeline([]); }
      else setTimeline(data || []);
      setLoading(false);
    };
    loadTimeline();
  }, [sportId]);

  const totals = useMemo(() => timeline.reduce((acc, row) => ({
    cards: acc.cards + Number(row.canonical_cards || 0),
    families: acc.families + Number(row.family_count || 0),
    releases: acc.releases + Number(row.release_count || 0),
  }), { cards: 0, families: 0, releases: 0 }), [timeline]);

  const selectYear = async (row) => {
    setSelected(row); setDetailLoading(true); setFamilies([]);
    const { data, error: rpcError } = await supabase.rpc("get_market_insights_year_families", { p_year: Number(row.year), p_subcategory_id: sportId || null, p_limit: 25 });
    if (rpcError) { console.error(rpcError); setError("The year breakdown could not be loaded."); }
    else setFamilies(data || []);
    setDetailLoading(false);
  };

  const selectedSport = sports.find((s) => String(s.subcategory_id) === String(sportId));

  return (
    <main className="market-insights">
      <InsightsBackButton to="/insights/market" label="Market Insights" />
      <section className="market-hero">
        <div><span className="market-eyebrow">Hobby Timeline</span><h1>Cards Cataloged by Year</h1><p>Explore how the number of unique cards cataloged in Hobbli changes across the history of the hobby. Filter by sport and select any year to see the sets and brands behind the total.</p></div>
        <div className="market-hero-icon"><FaChartLine /></div>
      </section>

      <section className="market-controls">
        <div><strong>Cards Cataloged by Year</strong><span>{selectedSport ? selectedSport.subcategory_name : "All sports"}</span></div>
        <label><span>Sport</span><select value={sportId} onChange={(e) => setSportId(e.target.value)}><option value="">All sports</option>{sports.map((sport) => <option key={sport.subcategory_id} value={sport.subcategory_id}>{sport.subcategory_name}</option>)}</select></label>
      </section>

      {error && <div className="market-error">{error}</div>}
      {loading ? <div className="market-loading">Building the Hobbli catalog timeline…</div> : timeline.length ? <>
        <section className="market-stat-grid">
          <article><span>Cards cataloged</span><strong>{integer.format(totals.cards)}</strong></article>
          <article><span>Years cataloged</span><strong>{integer.format(timeline.length)}</strong></article>
          <article><span>Sets cataloged</span><strong>{integer.format(totals.families)}</strong></article>
          <article><span>Checklists cataloged</span><strong>{integer.format(totals.releases)}</strong></article>
        </section>
        <section className="market-panel">
          <div className="market-panel-heading"><div><span className="market-eyebrow">Historical timeline</span><h2>Cards Cataloged by Year</h2></div><span>Click a point to explore that year</span></div>
          <TimelineChart rows={timeline} selectedYear={selected?.year} onSelectYear={selectYear} />
          <p className="market-method-note"><FaDatabase /> Counts represent unique cards currently cataloged in Hobbli. Variants and parallels are not included in these totals. Years with incomplete catalog coverage may appear lower than the hobby actually was.</p>
        </section>
      </> : <div className="market-loading">No cataloged cards were found for this filter.</div>}

      {selected && <section className="market-panel market-year-panel">
        <div className="market-panel-heading"><div><span className="market-eyebrow">Year drilldown</span><h2>{selected.year} {selectedSport?.subcategory_name || "All sports"}</h2></div><div className="market-year-card-total"><strong>{integer.format(selected.canonical_cards)}</strong><span>unique cards cataloged</span></div></div>
        <p className="market-year-note">Variants and parallels are not included in this card total.</p>
        <div className="market-year-stats"><span>{integer.format(selected.family_count)} sets</span><span>{integer.format(selected.release_count)} checklists</span><span>{integer.format(selected.brand_count)} brands</span></div>
        {detailLoading ? <div className="market-loading compact">Loading sets…</div> : <div className="market-family-list">
          {families.map((family) => <div className="market-family-row" key={`${family.release_family_id}-${family.family_name}`}><div><strong>{family.family_name}</strong><span>{family.brand}{family.subcategory_name ? ` · ${family.subcategory_name}` : ""}</span></div><b>{integer.format(family.canonical_cards)}</b></div>)}
          {!families.length && <div className="market-loading compact">No set-level breakdown is available for this year.</div>}
        </div>}
      </section>}
    </main>
  );
}
